//==================================================================================================
// Kitten Cat LLC ("COMPANY") CONFIDENTIAL                                                         =
// Unpublished Copyright (c) 2021 Kitten Cat LLC. All rights reserved.                             =
//                                                                                                 =
// NOTICE: All information contained herein is, and remains the property of COMPANY. The           =
// intellectual and technical concepts contained herein are proprietary to COMPANY and may be      =
// covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret      =
// or copyright law. Dissemination of this information or reproduction of this material is         =
// strictly forbidden unless prior written permission is obtained from COMPANY. Access to the      =
// source code contained herein is hereby forbidden to anyone except current COMPANY               =
// employees, managers, or contractors who have executed Confidentiality and Non-disclosure        =
// agreements explicitly covering such access.                                                     =
//                                                                                                 =
// The copyright notice above does not evidence any actual or intended publication or              =
// disclosure of this source code, which includes information that is confidential and/or          =
// proprietary, and is a trade secret, of COMPANY. ANY REPRODUCTION, MODIFICATION,                 =
// DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE CODE       =
// WITHOUT THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF      =
// APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE       =
// AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR        =
// DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE, IN      =
// WHOLE OR IN PART.                                                                               =
//==================================================================================================

import _delay from 'lodash/delay';
import _throttle from 'lodash/throttle';

export default class ScrollSpy {
    menuList: HTMLElement;
    options: {
        sectionClass: string;
        menuActiveTarget: string;
        offset: number;
        hrefAttribute: string;
        activeClass: string;
        scrollContainer: string;
    };
    scroller: HTMLElement;
    // eslint-disable-next-line no-undef
    sections: NodeListOf<HTMLElement>;
    afterScrollTimerId: number;

    constructor(menu, options = {}) {
        if (!menu) {
            throw new Error('First argument is query selector to your navigation.');
        }

        if (typeof options !== 'object') {
            throw new Error('Second argument must be instance of Object.');
        }

        const defaultOptions = {
            sectionClass: '.scrollspy',
            menuActiveTarget: 'li > a',
            offset: 0,
            hrefAttribute: 'href',
            activeClass: 'active',
            scrollContainer: '',
        };

        this.menuList = menu;
        this.options = { ...defaultOptions, ...options };
        this.scroller = document.querySelector(this.options.scrollContainer);
        this.sections = document.querySelectorAll(this.options.sectionClass);
        this.listen();
    }

    listen() {
        this.scroller.addEventListener('scroll', _throttle(() => {
            this.onScroll();
            if (this.afterScrollTimerId !== null) {
                clearTimeout(this.afterScrollTimerId);
                this.afterScrollTimerId = null;
            }
            this.afterScrollTimerId = _delay(() => {
                this.onScroll();
            }, 1000);
        }, 50), { passive: true });
    }

    onScroll() {
        const section = this.getSectionInView();
        const menuItem = this.getMenuItemBySection(section);

        if (menuItem) {
            this.removeCurrentActive({ ignore: menuItem });
            this.setActive(menuItem);
        }
    }

    getMenuItemBySection(section): HTMLElement | null {
        if (!section) {
            return null;
        }
        const sectionId = section.getAttribute('id');
        return this.menuList.querySelector(`[${this.options.hrefAttribute}="#${sectionId}"]`);
    }

    getSectionInView(): HTMLElement | null {
        for (let i = 0; i < this.sections.length; i += 1) {
            const startAt = this.sections[i].offsetTop;
            const endAt = startAt + this.sections[i].offsetHeight;
            let currentPosition = (document.documentElement.scrollTop || document.body.scrollTop) + this.options.offset;

            if (this.options.scrollContainer && this.scroller) {
                currentPosition = (this.scroller.scrollTop) + this.options.offset;
            }

            const isInView = currentPosition > startAt && currentPosition <= endAt;
            if (isInView) return this.sections[i];
        }
        return null;
    }

    setActive(activeItem: HTMLElement) {
        const isActive = activeItem.classList.contains(this.options.activeClass);
        if (!isActive) activeItem.classList.add(this.options.activeClass);
    }

    removeCurrentActive({ ignore }) {
        const { hrefAttribute, menuActiveTarget, activeClass } = this.options;
        const items = `${menuActiveTarget} 
            .${activeClass}:not([${hrefAttribute}="${ignore.getAttribute(hrefAttribute)}"])`;
        const menuItems = this.menuList.querySelectorAll(items);
        menuItems.forEach((item) => item.classList.remove(this.options.activeClass));
    }
}
